import React, { useState, useRef, useCallback } from 'react';
import { Bot, Send } from 'lucide-react';
import { streamListingAi } from '../../../util/api';
import { isNeverGonnaGiveYouUp, RICKROLL_VIDEO_URL } from '../../../util/easterEgg';
import css from './ListingAiCard.module.css';

/**
 * Smart prompt chip suggestions based on category and listing data.
 */
function getSmartChips(listing) {
  const { title = '', publicData = {} } = listing?.attributes || {};
  const category = publicData.categoryLevel1 || '';
  const t = title.toLowerCase();

  const chips = ['Is this a good deal?'];

  if (category.includes('gpu') || t.includes('rtx') || t.includes('gpu')) {
    chips.push('Enough VRAM for 4K gaming?', 'Compare to last gen');
  } else if (category.includes('phone') || t.includes('iphone') || t.includes('galaxy')) {
    chips.push('Battery life vs newer model?', 'Is it unlocked?');
  } else if (category.includes('laptop') || t.includes('macbook')) {
    chips.push('Good for coding/productivity?', 'How does this compare?');
  } else if (category.includes('cpu') || t.includes('ryzen') || t.includes('i7')) {
    chips.push('Compatible motherboards?', 'Good for gaming or workstation?');
  } else if (category.includes('console')) {
    chips.push('What comes included?', 'Compare to newer model');
  } else {
    chips.push('What should I check?', 'Compare to similar');
  }

  return chips;
}

/**
 * Build context object for the AI stream from listing data.
 */
function buildListingContext(listing, authorDisplayName) {
  const attrs = listing?.attributes || {};
  const pd = attrs.publicData || {};

  const specs = {};
  for (const [key, value] of Object.entries(pd)) {
    if (key.startsWith('device_') && value != null) {
      specs[key] = value;
    }
  }

  return {
    mode: 'listing_page',
    listing_id: listing?.id?.uuid,
    title: attrs.title,
    description: (attrs.description || '').slice(0, 500),
    price_pence: attrs.price?.amount,
    currency: attrs.price?.currency,
    condition: pd.device_condition,
    category: pd.categoryLevel1,
    seller: authorDisplayName || 'Unknown',
    specs,
  };
}

const ListingAiCard = ({ listing, authorDisplayName, className }) => {
  const [state, setState] = useState('idle');
  const [response, setResponse] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const abortRef = useRef(null);
  const responseRef = useRef('');

  const chips = getSmartChips(listing);
  const context = buildListingContext(listing, authorDisplayName);

  const sendMessage = useCallback(
    message => {
      const trimmed = message.trim();
      if (!trimmed) return;
      if (isNeverGonnaGiveYouUp(trimmed)) {
        window.location.href = RICKROLL_VIDEO_URL;
        return;
      }
      if (abortRef.current) abortRef.current();

      setState('loading');
      setResponse('');
      setInputValue('');
      responseRef.current = '';

      const history = [...conversationHistory, { role: 'user', content: message }];

      abortRef.current = streamListingAi(trimmed, {
        conversationHistory,
        context,
        callbacks: {
          onContent: text => {
            responseRef.current += text;
            setResponse(responseRef.current);
            setState('streaming');
          },
          onDone: event => {
            const finalText = event?.response || event?.message || responseRef.current;
            setResponse(finalText || responseRef.current);
            setState('done');
            setConversationHistory([
              ...history,
              { role: 'assistant', content: finalText || responseRef.current },
            ]);
          },
          onError: () => {
            setResponse('Sorry, I couldn\u2019t analyse this listing right now. Please try again.');
            setState('done');
          },
        },
      });
    },
    [conversationHistory, context]
  );

  const handleSubmit = e => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleReset = () => {
    if (abortRef.current) abortRef.current();
    setState('idle');
    setResponse('');
    setInputValue('');
    responseRef.current = '';
  };

  const isIdle = state === 'idle';
  const isLoading = state === 'loading';
  const isStreaming = state === 'streaming';
  const isDone = state === 'done';
  const isBusy = isLoading || isStreaming;

  return (
    <div className={[css.root, className].filter(Boolean).join(' ')}>
      {/* Header — matches Laurel */}
      <div className={css.header}>
        <Bot size={18} className={css.laurelIcon} />
        <span className={css.headerTitle}>Laurel</span>
      </div>

      {/* Prompt chips */}
      {(isIdle || isDone) && (
        <div className={css.chips}>
          {chips.map((label, i) => (
            <button key={i} className={css.chip} onClick={() => sendMessage(label)} type="button">
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Loading dots */}
      {isLoading && (
        <div className={css.loadingDots}>
          <span />
          <span />
          <span />
        </div>
      )}

      {/* Streaming / done response */}
      {(isStreaming || isDone) && response && (
        <div className={css.responseArea}>
          <div className={css.responseBubble}>
            {response}
            {isStreaming && <span className={css.shimmerCursor} />}
          </div>
        </div>
      )}

      {/* Reset */}
      {isDone && (
        <div className={css.resetRow}>
          <button className={css.resetButton} onClick={handleReset} type="button">
            Ask another question
          </button>
        </div>
      )}

      {/* Input */}
      <form className={css.inputRow} onSubmit={handleSubmit}>
        <input
          className={css.input}
          type="text"
          placeholder="Ask about this listing..."
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          disabled={isBusy}
        />
        <button className={css.sendButton} type="submit" disabled={isBusy || !inputValue.trim()}>
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};

export default ListingAiCard;
